import type { SvgViewBox } from '@highimpact-frontend/api-types';
import {
  buildSvgBackgroundMarkup,
  computeSvgConstrainedGridMetricsFromHeight,
} from '../state/dashboard-layout-operations';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import ReactGridLayout, { type Layout } from 'react-grid-layout';

// -- type definitions --

export type SvgConstrainedGridLayoutProps = {
  svgViewBox: SvgViewBox;
  svgData: string;
  layout: Layout[];
  className?: string;
  children: React.ReactNode;
};

// -- helper functions --

/**
 * Tells plot widgets that the dashboard size changed after the grid settles.
 */
const notifyViewportResize = (): void => {
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      window.dispatchEvent(new Event('resize'));
    });
  });
};

const readAvailableHeight = (entry: ResizeObserverEntry): number | null => {
  const { height } = entry.contentRect;
  if (height <= 0) {
    return null;
  }
  return height;
};

// -- main component --

/**
 * Renders a dashboard grid locked to an SVG background coordinate system.
 * The layout height follows the viewport; width is derived from the aspect ratio.
 */
export const SvgConstrainedGridLayout: React.FC<SvgConstrainedGridLayoutProps> = ({
  svgViewBox,
  svgData,
  layout,
  className,
  children,
}) => {
  const shellRef = useRef<HTMLDivElement>(null);
  const [availableHeight, setAvailableHeight] = useState(0);

  const svgBackgroundMarkup = useMemo(
    () => buildSvgBackgroundMarkup(svgData),
    [svgData]
  );

  const gridMetrics = useMemo(
    () =>
      computeSvgConstrainedGridMetricsFromHeight(availableHeight, svgViewBox),
    [availableHeight, svgViewBox]
  );

  useEffect(() => {
    const shellElement = shellRef.current;
    if (!shellElement) {
      return undefined;
    }

    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const nextHeight = readAvailableHeight(entry);
        if (nextHeight !== null) {
          setAvailableHeight(nextHeight);
        }
      }
    });

    resizeObserver.observe(shellElement);
    return () => resizeObserver.disconnect();
  }, []);

  useEffect(() => {
    if (gridMetrics.width <= 0 || gridMetrics.rowHeight <= 0) {
      return;
    }
    notifyViewportResize();
  }, [gridMetrics.width, gridMetrics.rowHeight]);

  return (
    <div ref={shellRef} className="dashboard-svg-constrained-shell">
      <div
        className={`dashboard-svg-constrained ${className ?? ''}`.trim()}
        style={{
          width: gridMetrics.width,
          height: gridMetrics.height,
        }}
      >
        <div
          className="dashboard-svg-constrained__background"
          dangerouslySetInnerHTML={{ __html: svgBackgroundMarkup }}
        />
        {gridMetrics.width > 0 && gridMetrics.rowHeight > 0 && (
          <ReactGridLayout
            className="dashboard-svg-constrained__grid"
            layout={layout}
            cols={svgViewBox.width}
            rowHeight={gridMetrics.rowHeight}
            width={gridMetrics.width}
            margin={[0, 0]}
            containerPadding={[0, 0]}
            compactType={null}
            preventCollision={true}
            isDraggable={false}
            isResizable={false}
            useCSSTransforms={true}
          >
            {children}
          </ReactGridLayout>
        )}
      </div>
    </div>
  );
};
